/**
 * Лента сданных отчётов дня в 2 колонки (задача 71 §5.2).
 *
 * Макап M6/D6 `feed`: header «Сданы · N» + агрегатные бейджи дня «▣ на
 * подтверждении» / «✓ подтверждено» (контекст-бар дня убран в §2.6 — счётчики
 * переехали сюда; «не сдали» несёт заголовок `MissingPanel`), карточки в две
 * колонки. Подтверждение/отмена — мутации §1.1 с оптимистичным апдейтом чипа
 * полосы (через `useApprove/UnapproveTimesheet`); ошибки → тост. Правка
 * («✎ Править») — заглушка-колбэк, реальная форма в §5.3 (док-панель).
 */
import { useTranslation } from "react-i18next";

import { Surface } from "@/components/ui/surface";
import { Badge } from "@/components/ui/badge";
import { showApiErrorToast } from "@/lib/api-error-toast";
import { useApproveTimesheet, useUnapproveTimesheet } from "@/hooks/useApproval";
import type { DayReportFeed } from "@/api/approval";
import { ReportCard } from "./ReportCard";

interface ReportFeedProps {
  feed: DayReportFeed;
  date: string;
  weekStart: string;
  unitId: number | null;
  currentUserId: number | null;
  /** Агрегаты выбранного дня (из `selectedDay`); бейдж виден только при > 0. */
  pendingCount: number;
  approvedCount: number;
  onEdit: (userId: number) => void;
  /** 2 (десктоп D6) или 1 (мобайл M6). Дефолт — 2. */
  columns?: 1 | 2;
}

export function ReportFeed({
  feed,
  date,
  weekStart,
  unitId,
  currentUserId,
  pendingCount,
  approvedCount,
  onEdit,
  columns = 2,
}: ReportFeedProps): React.ReactElement {
  const { t } = useTranslation();
  const approve = useApproveTimesheet();
  const unapprove = useUnapproveTimesheet();

  if (feed.cards.length === 0) {
    return (
      <Surface className="p-4 text-sm text-[var(--fg-3)]" data-testid="approval-feed-empty">
        {t("timeApproval.feedEmpty")}
      </Surface>
    );
  }

  const submitted = feed.cards.length;
  // Тот же subject не должен ловить двойной клик пока мутация в полёте.
  const inFlightUserId =
    approve.isPending && approve.variables
      ? approve.variables.userId
      : unapprove.isPending && unapprove.variables
        ? unapprove.variables.userId
        : null;

  return (
    <section className="flex flex-col gap-3" data-testid="approval-feed">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs font-condensed uppercase tracking-[0.06em] text-[var(--fg-3)]">
        <span className="h-px flex-1" style={{ background: "var(--rule-faint)" }} />
        <span>{t("timeApproval.feedHeader", { submitted })}</span>
        {pendingCount > 0 && (
          <Badge tone="caution">
            <Glyph>▣</Glyph>
            {t("timeApproval.ctxPending", { count: pendingCount })}
          </Badge>
        )}
        {approvedCount > 0 && (
          <Badge tone="success">
            <Glyph>✓</Glyph>
            {t("timeApproval.ctxApproved", { count: approvedCount })}
          </Badge>
        )}
        <span className="h-px flex-1" style={{ background: "var(--rule-faint)" }} />
      </div>

      <div
        className={
          columns === 2
            ? "grid grid-cols-1 items-start gap-3 xl:grid-cols-2"
            : "grid grid-cols-1 items-start gap-3"
        }
      >
        {feed.cards.map((card) => (
          <ReportCard
            key={card.user.id}
            card={card}
            isSelf={card.user.id === currentUserId}
            pending={inFlightUserId === card.user.id}
            onApprove={() => {
              approve.mutate(
                { userId: card.user.id, date, weekStart, unitId },
                {
                  onError: (err) => {
                    showApiErrorToast(err, {
                      fallbackMessage: t("timeApproval.approveError"),
                    });
                  },
                },
              );
            }}
            onUnapprove={() => {
              unapprove.mutate(
                { userId: card.user.id, date, weekStart, unitId },
                {
                  onError: (err) =>
                    showApiErrorToast(err, {
                      fallbackMessage: t("timeApproval.cancelError"),
                    }),
                },
              );
            }}
            onEdit={() => onEdit(card.user.id)}
            onOpenDay={() => onEdit(card.user.id)}
          />
        ))}
      </div>
    </section>
  );
}

/** Глиф-префикс бейджа (mono, не капсуется) — связка с разрезами чипа полосы. */
function Glyph({ children }: { children: React.ReactNode }): React.ReactElement {
  return (
    <span className="font-mono" aria-hidden="true">
      {children}
    </span>
  );
}
